// src/components/AppErrorBoundary.js
//
// GENEL HATA SINIRI — "tek bileşen çöker → tüm sayfa beyaz" sınıfını kapatır
// (12 Haz 2026 üretim kesintisi: Hikayeler TDZ hatası tüm uygulamayı
// beyazlatmıştı; kök/sekme/şerit/modal seviyelerinde boundary yoktu).
//
// Kullanım:
//   <AppErrorBoundary variant="full">   → markalı tam ekran + Yenile (kök)
//   <AppErrorBoundary variant="inline"> → kompakt kart + Tekrar dene (sekme)
//   <AppErrorBoundary variant="silent"> → null render (Hikayeler şeridi, modal)
//   key={...} verilerek bağlam değişiminde boundary resetlenir.
//
// Renkler SABİT (açık tema politikası).

import React from 'react';
import { captureExceptionSafe } from '../sentryClient';
import { IkonKoprusu } from "../icon-system/runtime/IkonKoprusu";

interface AppErrorBoundaryProps {
  children?: React.ReactNode;
  variant?: 'full' | 'inline' | 'silent';
  label?: string;
  onError?: (error: unknown) => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export default class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  isChunkError?: boolean;

  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: any, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', this.props.label || '', error, info?.componentStack);

    // ChunkLoadError ÖZEL DURUM: lazy chunk hatası boundary'ye düştüğünde
    // global window-error reload koruması TETİKLENMEZ (React yakalamış olur)
    // ve "Tekrar dene" işe yaramaz (React.lazy başarısız import'u cache'ler).
    // Eski sekme + yeni deploy senaryosunda tek doğru hamle: sayfayı 1 kez
    // yenile (taze index → taze chunk hash'leri). Döngü koruması ortak bayrak.
    const msg = String(error?.name || error?.message || '');
    if (msg.includes('ChunkLoadError') || msg.includes('Loading chunk')) {
      this.isChunkError = true;
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem('mylasa-chunk-reload') === '1';
        if (!alreadyReloaded) sessionStorage.setItem('mylasa-chunk-reload', '1');
      } catch { /* private mode */ }
      if (!alreadyReloaded) {
        window.location.reload();
        return;
      }
    }

    captureExceptionSafe(error, {
      tags: { boundary: this.props.label || this.props.variant || 'unknown' },
    });
    this.props.onError?.(error);
  }

  reset = () => {
    // Chunk hatasında state reset'i çözüm değil — tam yenileme gerekir.
    if (this.isChunkError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const variant = this.props.variant || 'inline';
    if (variant === 'silent') return null;

    if (variant === 'full') {
      return (
        <div style={{
          minHeight: '100dvh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 14,
          background: '#ffffff', color: '#262626', textAlign: 'center', padding: 24,
        }}>
          <div style={{ fontSize: 44 }}>😵</div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Bir şeyler ters gitti</h2>
          <p style={{ margin: 0, maxWidth: 340, color: '#8e8e8e', fontSize: 14 }}>
            Beklenmedik bir hata oluştu. Sayfayı yenilemek genellikle çözer.
          </p>
          <button
            className="pressable"
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 28px', border: 'none', borderRadius: 12,
              background: 'linear-gradient(90deg, #7000ff, #00bfff)',
              color: '#ffffff', fontWeight: 800, fontSize: 15, cursor: 'pointer',
            }}
          >
            Yenile
          </button>
        </div>
      );
    }

    // inline — sekme içeriği çöktü; nav/diğer sekmeler yaşıyor
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 10, padding: '48px 24px',
        color: '#262626', textAlign: 'center',
      }}>
        <div style={{ fontSize: 34 }}><IkonKoprusu aile="system.warning" eski="⚠️" /></div>
        <strong style={{ fontSize: 16 }}>Bu bölüm yüklenemedi</strong>
        <p style={{ margin: 0, maxWidth: 320, color: '#8e8e8e', fontSize: 13 }}>
          Diğer sekmeler çalışmaya devam ediyor.
        </p>
        <button
          className="pressable"
          type="button"
          onClick={this.reset}
          style={{
            padding: '9px 20px', border: '1.5px solid #dbdbdb', borderRadius: 10,
            background: '#fafafa', color: '#262626', fontWeight: 700,
            fontSize: 13, cursor: 'pointer',
          }}
        >
          Tekrar dene
        </button>
      </div>
    );
  }
}
